import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  formatRupiahFull,
  formatRupiahShort,
  formatTanggalID,
} from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp,
  TrendingDown,
  HandCoins,
  PiggyBank,
  ArrowLeftRight,
  Target,
  Sparkles,
  ShieldCheck,
  ChevronRight,
  Bell,
  Plus,
  LogOut,
  Users,
} from "lucide-react";
import TransaksiForm from "@/pages/forms/TransaksiForm";
import HutangForm from "@/pages/forms/HutangForm";
import TabunganForm from "@/pages/forms/TabunganForm";
import RekeningForm from "@/pages/forms/RekeningForm";
import BayarHutangForm from "@/pages/forms/BayarHutangForm";

export default function Beranda() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null);
  const [showHealthDetail, setShowHealthDetail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, r, tab] = await Promise.all([
        api.get("/dashboard"),
        api.get("/rekening"),
        api.get("/tabungan"),
      ]);
      setData({ ...d.data, rekening: r.data, tabungan: tab.data });
      console.log("rekening loaded:", r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groupedLast = useMemo(() => {
    if (!data?.last_transaksi) return [];
    const g = data.last_transaksi.reduce((acc, t) => {
      (acc[t.tanggal] = acc[t.tanggal] || []).push(t);
      return acc;
    }, {});
    return Object.keys(g).sort((a, b) => b.localeCompare(a)).map((k) => ({ tanggal: k, items: g[k] }));
  }, [data]);

  // Goals dengan setoran > 0
  const activeGoals = useMemo(() => {
    if (!data?.tabungan) return [];
    return data.tabungan.filter((t) => t.terkumpul > 0);
  }, [data]);

  if (loading || !data) {
    return (
      <div className="p-6 text-slate-500 text-sm" data-testid="beranda-loading">
        Memuat dashboard...
      </div>
    );
  }

  const netWorth = data.net_worth || 0;
  const netWorthDelta = data.net_worth_delta || 0;
  const healthScore = data.health_score || 0;
  const misi = data.misi_aktif;
  const insight = data.ai_insight;
  const budget = data.budget;
  const healthComp = data.health_components || {};

  const healthColor = healthScore >= 80 ? "#059669" : healthScore >= 60 ? "#D97706" : healthScore >= 40 ? "#EA580C" : "#EF4444";
  const healthLabel = healthScore >= 80 ? "Sangat Sehat 💚" : healthScore >= 60 ? "Sehat 🟡" : healthScore >= 40 ? "Perlu Perhatian 🟠" : "Kritis 🔴";

  // Lingkaran health score
  const r = 23;
  const circ = 2 * Math.PI * r;
  const dash = (healthScore / 100) * circ;

  return (
    <div className="pb-4" style={{ background: "#F0F4FF", minHeight: "100vh" }} data-testid="beranda-page">

      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <div>
          <div className="text-base font-semibold" style={{ color: "#1a1a2e" }}>
            Halo, {user?.name || "Lucky"}! 👋
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
            Semangat kelola keuangan hari ini
          </div>
        </div>
        <button
          onClick={logout}
          className="w-9 h-9 rounded-full bg-white border flex items-center justify-center"
          style={{ borderColor: "#E5E7EB" }}
        >
          <LogOut size={16} style={{ color: "#6B7280" }} />
        </button>
      </div>

      {/* Net Worth Card */}
      <div className="px-4 mb-3">
        <div
          className="rounded-2xl p-5 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #3B82F6 100%)" }}
          data-testid="total-saldo-card"
        >
          {/* Health Score Badge */}
          <button
            onClick={() => setShowHealthDetail(true)}
            className="absolute top-4 right-4 w-14 h-14 flex flex-col items-center justify-center"
          >
            <svg width="54" height="54" style={{ position: "absolute", top: 0, left: 0 }}>
              <circle cx="27" cy="27" r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
              <circle
                cx="27" cy="27" r={r} fill="none"
                stroke={healthColor} strokeWidth="4"
                strokeDasharray={`${dash} ${circ}`}
                strokeLinecap="round"
                transform="rotate(-90 27 27)"
              />
            </svg>
            <span className="text-white font-bold text-sm relative z-10">{healthScore}</span>
            <span className="text-white/70 text-[8px] relative z-10">/100</span>
          </button>

          <div className="text-[11px] uppercase tracking-widest text-white/75 font-medium">
            Total kekayaan
          </div>
          <div className="text-3xl font-bold mt-1 tracking-tight" data-testid="total-saldo-value">
            {formatRupiahShort(netWorth)}
          </div>
          <div className="mt-1 mb-4">
            <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: "rgba(255,255,255,0.2)" }}>
              {netWorthDelta >= 0 ? "↑" : "↓"} {formatRupiahShort(Math.abs(netWorthDelta))} dari bulan lalu
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="text-[10px] text-white/70 font-medium">Aset total</div>
              <div className="text-sm font-bold mt-1">{formatRupiahShort(data.total_saldo)}</div>
            </div>
            <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="text-[10px] text-white/70 font-medium">Total hutang</div>
              <div className="text-sm font-bold mt-1">{formatRupiahShort(data.total_hutang_aktif)}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl p-2" style={{ background: "rgba(255,255,255,0.12)" }}>
              <div className="text-[9px] text-white/65">Masuk bln ini</div>
              <div className="text-xs font-bold mt-1" data-testid="masuk-bulan-ini">{formatRupiahShort(data.masuk_bulan_ini)}</div>
            </div>
            <div className="rounded-xl p-2" style={{ background: "rgba(255,255,255,0.12)" }}>
              <div className="text-[9px] text-white/65">Keluar bln ini</div>
              <div className="text-xs font-bold mt-1" data-testid="keluar-bulan-ini">{formatRupiahShort(data.keluar_bulan_ini)}</div>
            </div>
            <div className="rounded-xl p-2" style={{ background: "rgba(255,255,255,0.12)" }}>
              <div className="text-[9px] text-white/65">Keluar hari ini</div>
              <div className="text-xs font-bold mt-1" data-testid="keluar-hari-ini">{formatRupiahShort(data.keluar_hari_ini || 0)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Score Detail Bottom Sheet */}
      {showHealthDetail && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowHealthDetail(false)}
        >
          <div
            className="bg-white rounded-t-2xl p-5 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-9 h-1 rounded-full bg-slate-300 mx-auto mb-4" />
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-bold text-slate-900">Financial Health Score</div>
              <div className="text-2xl font-bold" style={{ color: healthColor }}>{healthScore}<span className="text-sm text-slate-400">/100</span></div>
            </div>
            <div className="text-xs font-semibold mb-3 px-2 py-1 rounded-full inline-block" style={{ background: "#F0FDF4", color: healthColor }}>{healthLabel}</div>
            <div className="space-y-3 mt-3">
              {[
                { label: "Debt Score", desc: "Rasio hutang vs aset", val: healthComp.debt_score || 0, color: "#EF4444" },
                { label: "Saving Score", desc: "Progress goals tabungan", val: healthComp.saving_score || 0, color: "#2563EB" },
                { label: "Cashflow Score", desc: "Pemasukan vs pengeluaran", val: healthComp.cashflow_score || 0, color: "#059669" },
                { label: "Emergency Fund", desc: "Dana darurat (3x pengeluaran)", val: healthComp.emergency_score || 0, color: "#D97706" },
              ].map((c) => (
                <div key={c.label}>
                  <div className="flex justify-between mb-1">
                    <div>
                      <div className="text-xs font-semibold text-slate-800">{c.label}</div>
                      <div className="text-[10px] text-slate-500">{c.desc}</div>
                    </div>
                    <div className="text-sm font-bold" style={{ color: c.color }}>{c.val}</div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${c.val}%`, background: c.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Strip Rekening */}
      <div className="mb-3">
        <div className="px-4 flex items-center justify-between mb-2">
          <div className="text-sm font-bold" style={{ color: "#111827" }}>Rekening</div>
          <button onClick={() => nav("/rekening")} className="text-xs font-semibold" style={{ color: "#2563EB" }}>Lihat semua</button>
        </div>
        <div className="pl-4 flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {data.rekening.map((r) => (
            <div key={r.id} className="flex-shrink-0 min-w-[120px] rounded-xl px-3 py-2.5 bg-white border" style={{ borderColor: "#E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div className="text-[10px] font-medium" style={{ color: "#9CA3AF" }}>{r.bank}</div>
              <div className="text-xs font-bold truncate mt-0.5" style={{ color: "#374151" }}>{r.nama}</div>
              <div className="text-sm font-bold mt-1" style={{ color: "#1E3A8A" }}>{formatRupiahShort(r.saldo)}</div>
            </div>
          ))}
          <button onClick={() => setOpenForm("rekening")} className="flex-shrink-0 min-w-[90px] rounded-xl px-3 py-2.5 border-2 border-dashed flex flex-col items-center justify-center gap-1" style={{ borderColor: "#CBD5E1" }}>
            <Plus size={16} style={{ color: "#94A3B8" }} />
            <span className="text-[10px] font-semibold" style={{ color: "#94A3B8" }}>Tambah</span>
          </button>
          <div className="w-2 flex-shrink-0" />
        </div>
      </div>

      {/* Misi Saat Ini */}
      {misi && (
        <div className="px-4 mb-3">
          <div className="bg-white rounded-2xl p-4" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}>
                <Target size={18} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mb-1" style={{ background: "#EEF2FF", color: "#2563EB" }}>
                  Misi saat ini — prioritas #1
                </div>
                <div className="text-sm font-bold truncate" style={{ color: "#111827" }}>{misi.nama}</div>
                {misi.jatuh_tempo && (
                  <div className="text-[11px] font-medium mt-0.5" style={{ color: "#EF4444" }}>
                    Jatuh tempo: {formatTanggalID(misi.jatuh_tempo)}
                  </div>
                )}
              </div>
            </div>
            <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: "#F3F4F6" }}>
              <div className="h-full rounded-full" style={{ width: `${misi.progress_pct}%`, background: "linear-gradient(90deg, #2563EB, #7C3AED)" }} />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs" style={{ color: "#6B7280" }}>
                Sisa: <span className="font-bold" style={{ color: "#111827" }}>{formatRupiahFull(misi.sisa)}</span>
              </div>
              <button
                onClick={() => setOpenForm(misi.tipe === "hutang" ? "bayar-hutang" : "setor-tabungan")}
                className="text-xs font-bold text-white px-3 py-1.5 rounded-full"
                style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}
              >
                {misi.tipe === "hutang" ? "Bayar sekarang" : "Setor sekarang"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Insight */}
      {insight && (
        <div className="px-4 mb-3">
          <div className="rounded-2xl p-4 flex gap-3 items-start" style={{ background: "linear-gradient(135deg, #EEF2FF, #F5F3FF)", border: "0.5px solid #C7D2FE" }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}>
              <Sparkles size={16} color="white" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#4F46E5" }}>AI Insight</div>
              <div className="text-xs leading-relaxed" style={{ color: "#1F2937" }}>{insight.pesan}</div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Guardian */}
      {budget && (
        <div className="px-4 mb-3">
          <div className="bg-white rounded-2xl p-4" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#FFF7ED" }}>
                  <ShieldCheck size={16} style={{ color: "#EA580C" }} />
                </div>
                <div>
                  <div className="text-xs font-bold" style={{ color: "#111827" }}>Budget Guardian</div>
                  <div className="text-[10px]" style={{ color: "#6B7280" }}>Operasional bulan ini</div>
                </div>
              </div>
              <div className="text-[10px] font-semibold" style={{ color: "#2563EB" }}>Ubah</div>
            </div>
            <div className="flex items-end justify-between mb-2">
              <div className="text-xl font-bold" style={{ color: "#EA580C" }}>{formatRupiahShort(budget.terpakai)}</div>
              <div className="text-xs" style={{ color: "#6B7280" }}>dari {formatRupiahShort(budget.operasional)}</div>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden mb-2" style={{ background: "#FEF3C7" }}>
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, budget.pct)}%`, background: "linear-gradient(90deg, #F59E0B, #EA580C)" }} />
            </div>
            <div className="flex justify-between">
              <div className="text-xs font-bold" style={{ color: "#EA580C" }}>{budget.pct}% terpakai</div>
              <div className="text-xs" style={{ color: "#6B7280" }}>
                Maks/hari: <span className="font-bold" style={{ color: "#111827" }}>{formatRupiahShort(budget.maks_per_hari)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Target Aktif */}
      {activeGoals.length > 0 && (
        <div className="px-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold" style={{ color: "#111827" }}>Target aktif</div>
            <button onClick={() => nav("/tabungan")} className="text-xs font-semibold" style={{ color: "#2563EB" }}>Lihat semua</button>
          </div>
          <div className="bg-white rounded-2xl p-4 space-y-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            {activeGoals.map((t) => {
              const pct = Math.min(100, Math.round((t.terkumpul / t.target) * 100));
              return (
                <div key={t.id} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #059669, #10B981)" }}>
                    <PiggyBank size={18} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold truncate" style={{ color: "#111827" }}>{t.nama}</div>
                      <div className="text-sm font-bold ml-2" style={{ color: "#059669" }}>{pct}%</div>
                    </div>
                    {t.deadline && <div className="text-[10px] mb-1" style={{ color: "#6B7280" }}>Deadline: {formatTanggalID(t.deadline)}</div>}
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#F3F4F6" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #059669, #10B981)" }} />
                    </div>
                    <div className="flex justify-between mt-1">
                      <div className="text-[10px]" style={{ color: "#6B7280" }}>{formatRupiahShort(t.terkumpul)} terkumpul</div>
                      <div className="text-[10px]" style={{ color: "#6B7280" }}>Sisa {formatRupiahShort(Math.max(0, t.target - t.terkumpul))}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hutang Aktif */}
      <div className="px-4 mb-3">
        <div
          className="rounded-2xl p-4"
          style={{ background: data.jumlah_hutang_aktif > 0 ? "#fff" : "#F0FDF4", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          data-testid="hutang-summary-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6B7280" }}>
              {data.jumlah_hutang_aktif > 0 ? `Sisa hutang (${data.jumlah_hutang_aktif} aktif)` : "Bebas hutang 🎉"}
            </div>
            <div className="text-lg font-bold" style={{ color: data.jumlah_hutang_aktif > 0 ? "#EF4444" : "#059669" }}>
              {formatRupiahShort(data.total_hutang_aktif)}
            </div>
          </div>
          {data.hutang_aktif.length > 0 && (
            <div className="space-y-2.5 mb-3">
              {data.hutang_aktif.map((h) => {
                const pct = Math.min(100, Math.round(((h.total - h.sisa) / h.total) * 100));
                return (
                  <div key={h.id}>
                    <div className="flex justify-between mb-1">
                      <div className="text-xs font-medium truncate" style={{ color: "#374151" }}>{h.deskripsi}</div>
                      <div className="text-xs font-bold ml-2" style={{ color: "#EF4444" }}>{formatRupiahShort(h.sisa)}</div>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#FEE2E2" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #EF4444, #F97316)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="rounded-xl p-3 grid grid-cols-2 gap-3" style={{ background: "#FFF7ED" }} data-testid="bayar-hutang-summary-box">
            <div>
              <div className="text-[10px] font-semibold" style={{ color: "#92400E" }}>Bayar hutang bulan ini</div>
              <div className="text-sm font-bold mt-0.5" style={{ color: "#B45309" }} data-testid="bayar-hutang-bulan-ini-value">
                {formatRupiahFull(data.bayar_hutang_bulan_ini || 0)}
              </div>
            </div>
            <div className="border-l pl-3" style={{ borderColor: "#FED7AA" }}>
              <div className="text-[10px] font-semibold" style={{ color: "#92400E" }}>Total (all-time)</div>
              <div className="text-sm font-bold mt-0.5" style={{ color: "#B45309" }} data-testid="total-bayar-hutang-value">
                {formatRupiahFull(data.total_bayar_hutang || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Aksi Cepat */}
      <div className="px-4 mb-3">
        <div className="text-sm font-bold mb-2" style={{ color: "#111827" }}>Aksi cepat</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Pemasukan", icon: TrendingUp, bg: "#ECFDF5", fg: "#059669", key: "pemasukan" },
            { label: "Pengeluaran", icon: TrendingDown, bg: "#FEF2F2", fg: "#EF4444", key: "pengeluaran" },
            { label: "Transfer", icon: ArrowLeftRight, bg: "#EFF6FF", fg: "#2563EB", key: "transfer" },
            { label: "Hutang", icon: HandCoins, bg: "#FFF7ED", fg: "#EA580C", key: "hutang" },
            { label: "Tabungan", icon: PiggyBank, bg: "#F5F3FF", fg: "#7C3AED", key: "tabungan" },
            { label: "Monitor", icon: Users, bg: "#EFF6FF", fg: "#2563EB", key: "monitor" },
          ].map(({ label, icon: Icon, bg, fg, key }) => (
            <button
              key={key}
              onClick={() => key === "monitor" ? nav("/monitor") : setOpenForm(key)}
              className="rounded-xl p-3 flex flex-col items-center gap-2"
              style={{ background: bg, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
            >
              <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center">
                <Icon size={18} style={{ color: fg }} />
              </div>
              <div className="text-[10px] font-bold" style={{ color: fg }}>{label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Transaksi Terakhir */}
      <div className="px-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold" style={{ color: "#111827" }}>Transaksi terakhir</div>
          <button onClick={() => nav("/transaksi")} className="text-xs font-semibold" style={{ color: "#2563EB" }}>Lihat semua</button>
        </div>
        {groupedLast.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 text-center text-xs border" style={{ color: "#6B7280", borderColor: "#E5E7EB" }}>
            Belum ada transaksi. Mulai catat lewat tombol aksi cepat di atas.
          </div>
        ) : (
          <div className="space-y-3" data-testid="beranda-transaksi-list">
            {groupedLast.map(({ tanggal, items }) => (
              <div key={tanggal}>
                <div className="text-[11px] font-semibold mb-1" style={{ color: "#6B7280" }}>{formatTanggalID(tanggal)}</div>
                <div className="bg-white rounded-2xl overflow-hidden border" style={{ borderColor: "#F3F4F6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                  {items.map((t, i) => {
                    const isTransfer = t.tipe === "transfer";
                    return (
                      <div
                        key={t.id}
                        className={`flex items-center justify-between px-3 py-2.5 ${i > 0 ? "border-t" : ""}`}
                        style={{ borderColor: "#F3F4F6", background: isTransfer ? "#EFF6FF" : "white" }}
                      >
                        <div className="min-w-0 pr-2">
                          <div className="text-xs font-semibold truncate" style={{ color: "#111827" }}>{t.deskripsi}</div>
                          <div className="text-[10px] truncate mt-0.5" style={{ color: "#9CA3AF" }}>
                            {isTransfer ? `${t.rekening_asal_nama} → ${t.rekening_tujuan_nama}` : t.rekening_nama}
                          </div>
                        </div>
                        <div className="text-xs font-bold whitespace-nowrap" style={{
                          color: isTransfer ? "#2563EB" : t.tipe === "pemasukan" ? "#059669" : "#EF4444"
                        }}>
                          {isTransfer ? formatRupiahFull(t.nominal) : `${t.tipe === "pemasukan" ? "+" : "-"}${formatRupiahFull(t.nominal)}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Forms */}
      <TransaksiForm
        open={openForm === "pemasukan" || openForm === "pengeluaran" || openForm === "transfer"}
        onClose={() => setOpenForm(null)}
        defaultTipe={openForm === "pemasukan" ? "pemasukan" : openForm === "transfer" ? "transfer" : "pengeluaran"}
        rekeningList={data.rekening}
        onSaved={load}
      />
      {openForm === "hutang" && console.log("rekening di beranda:", data.rekening)}
      <HutangForm open={openForm === "hutang"} onClose={() => setOpenForm(null)} onSaved={load} rekeningList={data.rekening || []} />
      <TabunganForm open={openForm === "tabungan"} onClose={() => setOpenForm(null)} onSaved={load} />
      <RekeningForm open={openForm === "rekening"} onClose={() => setOpenForm(null)} onSaved={load} />
      <BayarHutangForm
        open={openForm === "bayar-hutang"}
        onClose={() => setOpenForm(null)}
        hutangList={data.hutang_aktif || []}
        rekeningList={data.rekening || []}
        onSaved={load}
      />
    </div>
  );
}
